import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Send, XCircle, Ban, RefreshCw, CheckCircle2, BadgeDollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  requested: "bg-amber-100 text-amber-800", issued: "bg-green-100 text-green-800",
  declined: "bg-slate-200 text-slate-700", revoked: "bg-red-100 text-red-700", paid: "bg-blue-100 text-blue-800",
};
const ACCOUNTS = [["jmmb_usd", "JMMB Jamaica — USD"], ["jmmb_jmd", "JMMB Jamaica — JMD"], ["wells_fargo_us", "Wells Fargo (US) — USD"]];
const today = () => new Date().toISOString().slice(0, 10);

const MarkPaidForm = ({ pf, onSubmit, onCancel }) => {
  const [v, setV] = useState({
    amount_received: pf.total, payment_reference: "", payment_date: today(),
    paid_to_account: pf.bank_choice === "us" ? "wells_fargo_us" : "jmmb_usd",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
  const L = ({ children }) => <span className="text-[11px] font-medium text-muted-foreground">{children}</span>;
  return (
    <div className="grid sm:grid-cols-4 gap-2 bg-slate-50 p-3 rounded-lg" data-testid={`proforma-paid-form-${pf.number}`}>
      <label className="space-y-1"><L>Amount received (USD)</L><Input type="number" step="0.01" value={v.amount_received} onChange={set("amount_received")} data-testid="mark-paid-amount" /></label>
      <label className="space-y-1"><L>Payment reference *</L><Input value={v.payment_reference} onChange={set("payment_reference")} placeholder="e.g. wire ref" data-testid="mark-paid-reference" /></label>
      <label className="space-y-1"><L>Payment date</L><Input type="date" value={v.payment_date} onChange={set("payment_date")} data-testid="mark-paid-date" /></label>
      <label className="space-y-1"><L>Paid into</L>
        <select className="w-full border rounded-md px-2 text-sm h-10 bg-white" value={v.paid_to_account} onChange={set("paid_to_account")} data-testid="mark-paid-account">
          {ACCOUNTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      {Number(v.amount_received) < pf.total && <p className="sm:col-span-4 text-xs text-amber-700" data-testid="mark-paid-underpaid">Amount is less than the pro forma total — the order will be flagged as underpaid.</p>}
      <div className="sm:col-span-4 flex gap-2">
        <Button size="sm" className="bg-brand text-white" disabled={busy || !v.payment_reference.trim() || !(Number(v.amount_received) > 0)}
          onClick={async () => { setBusy(true); await onSubmit({ ...v, amount_received: Number(v.amount_received) }); setBusy(false); }} data-testid="mark-paid-submit">
          <CheckCircle2 className="h-4 w-4 mr-1" /> {busy ? "Saving…" : "Confirm Payment & Create Order"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

const Row = ({ pf, onAction, getAuthHeader }) => {
  const [note, setNote] = useState(pf.admin_note || "");
  const [paying, setPaying] = useState(false);
  const act = (path, body) => onAction(pf.id, path, body);
  const download = async () => {
    const r = await axios.get(`${API}/proforma/${pf.id}/pdf`, { headers: getAuthHeader(), responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: `${pf.number}.pdf` });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid={`proforma-row-${pf.number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading font-semibold">{pf.number} · {pf.company_name}</p>
          <p className="text-sm text-muted-foreground">{pf.contact_name} · {pf.email}{pf.phone ? ` · ${pf.phone}` : ""}</p>
          <p className="text-sm mt-1">{pf.participants} × {pf.course_title} ({pf.format_label}{pf.cohort_label ? ` · ${pf.cohort_label}` : ""})</p>
          {pf.notes && <p className="text-xs text-muted-foreground mt-1">Customer note: {pf.notes}</p>}
        </div>
        <div className="text-right space-y-1">
          <Badge className={STATUS[pf.status]} data-testid={`proforma-status-${pf.number}`}>{pf.status}</Badge>
          <p className="font-heading font-bold text-lg text-brand">{`USD ${Number(pf.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</p>
          <p className="text-xs text-muted-foreground">Bank: {pf.bank_choice === "us" ? "United States (Wells Fargo)" : "Jamaica (JMMB)"}</p>
          {pf.valid_until && <p className="text-xs text-muted-foreground">Valid until {pf.valid_until}</p>}
        </div>
      </div>
      {pf.status === "requested" && (
        <Textarea rows={2} placeholder="Optional note printed on the pro forma (e.g. includes PECB exam fees)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`proforma-note-${pf.number}`} />
      )}
      <div className="flex flex-wrap gap-2">
        {pf.status === "requested" && (
          <>
            <Button size="sm" className="bg-brand text-white" onClick={() => act("issue", { note })} data-testid={`proforma-issue-${pf.number}`}><Send className="h-4 w-4 mr-1" /> Approve & Send</Button>
            <Button size="sm" variant="outline" onClick={() => act("decline", { note })} data-testid={`proforma-decline-${pf.number}`}><XCircle className="h-4 w-4 mr-1" /> Decline</Button>
          </>
        )}
        {pf.status === "issued" && (
          <>
            <Button size="sm" className="bg-brand text-white" onClick={() => setPaying(true)} data-testid={`proforma-mark-paid-${pf.number}`}><BadgeDollarSign className="h-4 w-4 mr-1" /> Mark as Paid</Button>
            <Button size="sm" variant="outline" onClick={download} data-testid={`proforma-download-${pf.number}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
            <Button size="sm" variant="outline" onClick={() => act("issue", { note: pf.admin_note })} data-testid={`proforma-resend-${pf.number}`}><RefreshCw className="h-4 w-4 mr-1" /> Re-issue (new 30 days)</Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => act("revoke")} data-testid={`proforma-revoke-${pf.number}`}><Ban className="h-4 w-4 mr-1" /> Revoke</Button>
          </>
        )}
        {pf.status === "paid" && (
          <>
            <Button size="sm" variant="outline" onClick={download} data-testid={`proforma-download-${pf.number}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
            <Link to="/admin?section=orders"><Button size="sm" variant="outline" data-testid={`proforma-order-link-${pf.number}`}>Order #{pf.order_id?.slice(0, 8).toUpperCase()}</Button></Link>
            <span className="text-xs text-muted-foreground self-center">Paid {pf.paid_at?.slice(0, 10)} · ref {pf.payment_reference}</span>
            {pf.underpaid && <Badge className="bg-amber-100 text-amber-800" data-testid={`proforma-underpaid-${pf.number}`}>Underpaid</Badge>}
          </>
        )}
      </div>
      {paying && <MarkPaidForm pf={pf} onCancel={() => setPaying(false)} onSubmit={async (body) => { if (await act("mark-paid", body)) setPaying(false); }} />}
    </div>
  );
};

export const ProformaManager = ({ getAuthHeader }) => {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/proforma`, { headers: getAuthHeader() }).then((r) => setItems(r.data.proformas)).catch(() => toast.error("Failed to load pro formas"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onAction = async (id, path, body = {}) => {
    try {
      await axios.post(`${API}/proforma/${id}/${path}`, body, { headers: getAuthHeader() });
      toast.success({ issue: "Pro forma issued and emailed", "mark-paid": "Paid — order created and attendee request emailed" }[path] || `Pro forma ${path}d`);
      load();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
      return false;
    }
  };

  return (
    <Card data-testid="proforma-manager">
      <CardHeader><CardTitle className="font-heading">Pro Forma Invoices</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No pro forma requests yet.</p>}
        {items.map((pf) => <Row key={pf.id} pf={pf} onAction={onAction} getAuthHeader={getAuthHeader} />)}
      </CardContent>
    </Card>
  );
};
